import React from 'react';
import {
  Trophy,
  CircleDollarSign,
  TrendingUp,
  BrainCircuit,
  Calendar,
  Users,
  LineChart,
  BookOpen,
  Ghost,
  HelpCircle,
  Disc3,
  Medal,
  Star,
  Award,
  Flame,
  BarChart3,
  Target,
  Activity
} from 'lucide-react';
import SparkleButton from './ui/SparkleButton';
import { createTranslator } from '../utils/detectLanguage';
import { chatChipsPtBR } from './mappings/chatChips.pt-br';
import { chatChipsES } from './mappings/chatChips.es';

/**
 * Suggestion chips component optimized for the chat interface.
 * 
 * @param {function} onSuggestionClick - Function called when a chip is clicked
 * @param {string} variant - The set of suggestions to display (default or advanced)
 * @param {string} displayMode - Display mode: 'default', 'uniform' (for desktop grid layout)
 */
const ChatSuggestionChips = ({ onSuggestionClick, variant = 'default', displayMode = 'default' }) => {
  // Translation helper
  const t = createTranslator({
    'pt-br': chatChipsPtBR,
    'es': chatChipsES
  });
  
  // Define multiple suggestion sets for different contexts
  const suggestionSets = {
    default: [
      {
        icon: <HelpCircle className="w-4 h-4 text-emerald-400" />,
        label: "What's PickStar?",
        prompt: "Explain how to use PickStar, including how to get AI sports insights, what sports we cover, how to save analysis to my watchlist, and how to get sports analytics for better entertainment."
      },
      {
        icon: <TrendingUp className="w-4 h-4 text-emerald-400" />,
        label: "Daily Soccer Analysis",
        prompt: "5 high confidence soccer match analyses for today with goal scoring patterns"
      },
      {
        icon: <CircleDollarSign className="w-4 h-4 text-emerald-400" />,
        label: "Premier League",
        prompt: "Find any recent data points in your news for English Premier League that would help me analyze upcoming matches" 
      },
      { 
        icon: <Trophy className="w-4 h-4 text-emerald-400" />,
        label: "Análise BTTS",
        prompt: "Para os próximos jogos da Série A, quais partidas têm a maior probabilidade de ambas as equipes marcarem?"
      },
      { 
        icon: <BrainCircuit className="w-4 h-4 text-emerald-400" />,
        label: "Guide Me",
        prompt: "Pick an upcoming MLB game of your choice, and briefly explain how to analyze its key metrics."
      },
      { 
        icon: <Calendar className="w-4 h-4 text-emerald-400" />,
        label: "MLB Analytics",
        prompt: "Find a high value MLB game, and analyze with high confidence these metrics: Total bases trends; Player performance in Hits, Runs and RBIs; Team scoring patterns"
      },
    ],
    
    advanced: [
      {
        icon: <Users className="w-4 h-4 text-emerald-400" />,
        label: "Strategy Analysis",
        prompt: "Identify potential analytical opportunities for NBA basketball by comparing the point distributions and performance trends of upcoming games."
      },
      {
        icon: <LineChart className="w-4 h-4 text-emerald-400" />,
        label: "Team Metrics",
        prompt: "Analyze your news for insights to understand defensive efficiency of the top 5 NBA teams and how it correlates with their success."
      },
      { 
        icon: <Calendar className="w-4 h-4 text-emerald-400" />,
        label: "Upcoming NHL",
        prompt: "What are the most interesting NHL matchups this weekend and which have the best analytical opportunities?"
      },
      { 
        icon: <BookOpen className="w-4 h-4 text-emerald-400" />,
        label: "Analysis Methods",
        prompt: "Explain the concept of expected value in sports analytics and how to use it for finding good insights and opportunities."
      }
    ],
    
    // Soccer-focused suggestion set with multilingual support
    soccer: [
      {
        icon: <Trophy className="w-4 h-4 text-emerald-400" />,
        label: "Premier League",
        prompt: "What are the current top teams in the Premier League and which upcoming matches offer the best analytical opportunities?"
      },
      {
        icon: <Star className="w-4 h-4 text-emerald-400" />,
        label: "Série A (Português)",
        prompt: "Quais são os artilheiros do Brasileirão Série A nesta temporada e como isso afeta as análises estatísticas para os próximos jogos?"
      },
      {
        icon: <Medal className="w-4 h-4 text-emerald-400" />,
        label: "Série A (Português)",
        prompt: "Quais times do Brasileirão têm as melhores chances de classificação para a Libertadores baseado nas últimas performances e dados analíticos?"
      },
      {
        icon: <Flame className="w-4 h-4 text-emerald-400" />,
        label: "Liga MX (Español)",
        prompt: "¿Cuáles son los equipos con mejor desempeño defensivo en la Liga MX y cómo afecta esto al análisis de patrones de goles?"
      }
    ],

    // Additional analytics-focused suggestions
    analytics: [
      {
        icon: <BarChart3 className="w-4 h-4 text-emerald-400" />,
        label: "Performance Trends",
        prompt: "Analyze the recent performance trends of top teams across major leagues and identify key patterns."
      },
      {
        icon: <Target className="w-4 h-4 text-emerald-400" />,
        label: "Player Insights",
        prompt: "Provide detailed statistical analysis of top-performing players in their respective positions this season."
      },
      {
        icon: <Activity className="w-4 h-4 text-emerald-400" />,
        label: "Match Predictions",
        prompt: "Generate data-driven predictions for this weekend's most anticipated sports matchups."
      },
      {
        icon: <TrendingUp className="w-4 h-4 text-emerald-400" />,
        label: "Season Analysis",
        prompt: "Compare season statistics across different leagues and identify standout teams and players."
      }
    ]
  };

  // Get the correct set of suggestions and translate them
  const rawSuggestions = suggestionSets[variant] || suggestionSets.default;
  const suggestions = rawSuggestions.map(suggestion => ({
    ...suggestion,
    label: t(suggestion.label),
    prompt: t(suggestion.prompt)
  }));

  // For uniform grid display mode (desktop dropdown)
  // Mini-grid layout for mobile - compact 2-column grid
  if (displayMode === 'mini-grid') {
    return (
      <div className="grid grid-cols-2 gap-2">
        {suggestions.map((suggestion, index) => (
          <button
            key={index}
            onClick={() => onSuggestionClick(suggestion.prompt)}
            className="flex flex-col items-start p-2 bg-slate-800/80 hover:bg-emerald-800/30 
                      border border-slate-700 hover:border-emerald-500/30 rounded-lg 
                      text-white transition-colors
                      active:scale-95 transform duration-150"
          >
            <div className="flex items-center gap-1.5 mb-1">
              <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center bg-emerald-900/50 rounded-full">
                {suggestion.icon}
              </div>
              <span className="text-xs font-medium text-emerald-300 truncate">{suggestion.label}</span>
            </div>
            <span className="text-[10px] text-white/70 line-clamp-1">
              {suggestion.prompt.substring(0, 30)}...
            </span>
          </button>
        ))}
      </div>
    );
  }

  // Uniform grid display mode (desktop dropdown)
  if (displayMode === 'uniform') {
    return (
      <div className="grid grid-cols-2 gap-2">
        {suggestions.map((suggestion, index) => (
          <button
            key={index}
            onClick={() => onSuggestionClick(suggestion.prompt)}
            className="flex items-center gap-2 p-3 bg-slate-800/80 hover:bg-emerald-800/30 
                      border border-slate-700 hover:border-emerald-500/30 rounded-lg 
                      text-white transition-colors h-full
                      active:scale-95 transform duration-150"
          >
            <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-emerald-900/50 rounded-full p-1.5">
              {suggestion.icon}
            </div>
            <div className="flex flex-col items-start text-left">
              <span className="text-sm font-medium text-emerald-300">{suggestion.label}</span>
              <span className="text-xs text-white/70 line-clamp-1">
                {suggestion.prompt.length > 40 
                  ? `${suggestion.prompt.substring(0, 40)}...` 
                  : suggestion.prompt}
              </span>
            </div>
          </button>
        ))}
      </div>
    );
  }

  // Default display - flow layout
  return (
    <div className="flex flex-wrap justify-center gap-2 w-full">
      {suggestions.map((suggestion, index) => (
        <SparkleButton
          key={index}
          onClick={() => onSuggestionClick(suggestion.prompt)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg 
                     bg-white/5 hover:bg-white/10 
                     text-white/90 transition-colors
                     border border-white/10 backdrop-blur-sm
                     active:scale-95 transform duration-150"
        >
          {suggestion.icon}
          <span className="text-xs font-medium">{suggestion.label}</span>
        </SparkleButton>
      ))}
    </div>
  );
};

export default React.memo(ChatSuggestionChips);